import React, { useState } from 'react';
import {
  VSpacer,
  Button,
  Modal,
  Form,
  Input,
  PhoneInput,
  Select,
  CountrySelect,
  Text,
  HSpacer,
  Tooltip,
  Icon,
} from '@mqd/volt-base';
import PropTypes from 'prop-types';
import './AddBusinessDirectorModal.css';

export default function AddBusinessDirectorModal({
  hideModal,
  businessStore,
  canAddIdentifications,
  showSuccessToast,
  showErrorToast,
}) {
  const initialFormState = {
    firstName: null,
    middleName: null,
    lastName: null,
    title: null,
    email: null,
    phone: null,
    birthDate: null,
    nationality: null,
    placeOfBirth: null,
    address1: null,
    address2: null,
    city: null,
    state: null,
    country: null,
    postalCode: null,
    identificationsType: null,
    identificationsValue: null,
  };

  const [formState, setFormState] = useState({});
  const [addressCountryName, setAddressCountryName] = useState(formState.country || null);
  const [nationalityCountryName, setNationalityCountryName] = useState(
    formState.nationality || null
  );
  const [placeOfBirthCountryName, setPlaceOfBirthCountryName] = useState(
    formState.placeOfBirth || null
  );
  const [error, setError] = useState(null);
  const { addBusinessDirector } = businessStore;

  const handleFormChange = (newFormState) => {
    setFormState({
      ...newFormState,
      country: addressCountryName,
      nationality: nationalityCountryName,
      placeOfBirth: placeOfBirthCountryName,
    });
  };

  const handleSubmit = async (formState) => {
    const {
      firstName,
      middleName,
      lastName,
      title,
      email,
      phone,
      birthDate,
      nationality,
      placeOfBirth,
      address1,
      address2,
      city,
      state,
      country,
      postalCode,
      identificationsType,
      identificationsValue,
    } = formState;

    const address = {
      address1: address1,
      address2: address2,
      city: city,
      state: state,
      country: country,
      postal_code: postalCode,
    };

    const identifications =
      canAddIdentifications && identificationsType && identificationsValue
        ? [
            {
              type: identificationsType,
              value: identificationsValue,
            },
          ]
        : [];

    const newDirectorPayload = {
      firstName,
      middleName,
      lastName,
      title,
      email,
      phone,
      birthDate,
      nationality,
      placeOfBirth,
      address,
      identifications,
    };
    try {
      setError(null);
      const result = await addBusinessDirector(newDirectorPayload);
      if (result) {
        showSuccessToast();
      } else {
        showErrorToast();
      }
    } catch (error) {
      showErrorToast();
      setError(error);
    } finally {
      hideModal();
    }
  };

  return (
    <Modal
      testId={'add-business-director-modal'}
      heading={'Add Business Director'}
      error={error}
      hideModal={hideModal}
      footerButtons={[
        <Button onClick={() => handleSubmit(formState)} testId="add-business-director-button">
          Add
        </Button>,
      ]}
    >
      <div className="add-business-director-modal-group">
        <Form onFormChange={handleFormChange} initialState={initialFormState}>
          <Input
            label="First Name"
            placeholder="Enter First Name"
            name="firstName"
            maxLength={255}
          />
          <VSpacer factor={3} />
          <Input
            label="Middle Name"
            placeholder="Enter Middle Name"
            name="middleName"
            maxLength={40}
          />
          <VSpacer factor={3} />
          <Input label="Last Name" placeholder="Enter Last Name" name="lastName" maxLength={255} />
          <VSpacer factor={3} />
          <Input label="Title" placeholder="Enter Job Title" name="title" maxLength={255} />
          <VSpacer factor={3} />
          <Input label="Email" placeholder="Enter Email Address" name="email" maxLength={255} />
          <VSpacer factor={3} />
          <PhoneInput
            label="Phone"
            placeholder="Enter Phone Number"
            name="phone"
            maxLength={255}
            countryCodeTestId="add-business-director-phone-country-code"
            nationalNumberTestId="add-business-director-phone-national-number"
          />
          <VSpacer factor={3} />
          <Input label="Birth Date" placeholder="Enter Birth Date (YYYY-MM-DD)" name="birthDate" />
          <VSpacer factor={3} />
          <CountrySelect
            label="Nationality"
            name="nationality"
            placeholder={'Select country/region of nationality'}
            testId="add-business-director-nationality-country-select"
            width={'525px'}
            onChange={(option) => {
              const { alpha2 } = option;
              setNationalityCountryName(alpha2);
            }}
          />
          <VSpacer factor={3} />
          <CountrySelect
            label="Place of Birth"
            name="placeOfBirth"
            placeholder={'Select country/region of place of birth'}
            testId="add-business-director-place-of-birth-country-select"
            width={'525px'}
            onChange={(option) => {
              const { alpha2 } = option;
              setPlaceOfBirthCountryName(alpha2);
            }}
          />
          <VSpacer factor={3} />
          <Input
            label="Address Line 1"
            placeholder="Enter Address Line 1 of Residence"
            name="address1"
            maxLength={255}
          />
          <VSpacer factor={3} />
          <Input
            label="Address Line 2"
            placeholder="Enter Address Line 2 of Residence"
            name="address2"
            maxLength={255}
          />
          <VSpacer factor={3} />
          <Input label="City" placeholder="Enter City of Residence" name="city" maxLength={40} />
          <VSpacer factor={3} />
          <Input label="State" placeholder="Enter State of Residence" name="state" maxLength={35} />
          <VSpacer factor={3} />
          <CountrySelect
            label="Country"
            name="country"
            placeholder={'Select country/region of residence'}
            testId="add-business-director-address-country-select"
            width={'525px'}
            onChange={(option) => {
              const { alpha2 } = option;
              setAddressCountryName(alpha2);
            }}
          />
          <VSpacer factor={3} />
          <Input
            label="Postal Code"
            placeholder="Enter Postal Code of Residence"
            name="postalCode"
            maxLength={20}
          />
          {canAddIdentifications && (
            <>
              <VSpacer factor={3} />
              <Select
                label="Identification Type"
                placeholder="Enter Identification Type"
                options={['SSN', 'TIN', 'SIN', 'NIN']}
                name="identificationsType"
                fixOverflow={true}
              />
              <VSpacer factor={3} />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Text type="h6">Identification Value</Text>
                <HSpacer factor={0.5} />
                <Tooltip
                  portalZIndex={9999}
                  content={`
                        Forms of identification used to validate the business director. SSN values need to be 9 digits without dashes or spaces.
                      `}
                  width={'300px'}
                  fixOverflow={true}
                  testId="identification_value_tooltip"
                >
                  <Icon type="info" mod="disabled" hasTooltip />
                </Tooltip>
              </div>
              <VSpacer factor={1} />
              <Input
                placeholder="Enter Identification Value"
                name="identificationsValue"
                testId="identification_value_input"
              />
              <VSpacer factor={3} />
            </>
          )}
        </Form>
      </div>
    </Modal>
  );
}

AddBusinessDirectorModal.propTypes = {
  hideModal: PropTypes.func,
  renderFailedAlert: PropTypes.func,
  renderSuccessAlert: PropTypes.func,
  businessStore: PropTypes.object,
  canAddIdentifications: PropTypes.bool,
};

AddBusinessDirectorModal.defaultProps = {
  hideModal: () => {},
  renderFailedAlert: () => {},
  renderSuccessAlert: () => {},
  businessStore: {},
  canAddIdentifications: false,
};
